import ProviderAPIClient, { OrderingSessionGoneError } from '@/features/auth/ProviderAPIClient'
import { isDiscardMarkerSet, setDiscardMarker } from '@/features/ordering/discardMarker'
import { DISCARD_CONFLICT_MESSAGES, OrderingSessionDiscardConflictError } from '@/features/ordering/errors'
import { resolveDraftOwnerDisplayName } from '@/features/ordering/resolveDraftOwnerDisplayName'
import { useAppDispatch } from '@/store/hooks'
import { resetCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const DRAFT_DETAILS_UNAVAILABLE_MESSAGE =
  "Couldn't load this draft's details. Reload the page and try again — your draft is still saved."

export type DiscardAuthFlow = 'standalone' | 'epic'

export interface UseDiscardOrderParams {
  sessionId: string | null | undefined
  patientLookupId: string | null | undefined
  authFlow: DiscardAuthFlow
  draftOwnerUserId?: string | null
  currentUserId?: string | null
  cancelAutosave: () => void
  /** Clears page-local session/patient state (OrderStart / OrderReadyToSend). */
  onClearLocalOrderContext?: () => void
  /** True while parent is still loading draft owner id for the confirm dialog. */
  draftOwnerMetadataPending?: boolean
}

function applyDiscardMarker(patientLookupId: string | null | undefined): void {
  const id = patientLookupId?.trim()
  if (id) {
    setDiscardMarker(id)
  }
}

export function useDiscardOrder({
  sessionId,
  patientLookupId,
  authFlow,
  draftOwnerUserId,
  currentUserId,
  cancelAutosave,
  onClearLocalOrderContext,
  draftOwnerMetadataPending = false,
}: UseDiscardOrderParams) {
  const dispatch = useAppDispatch()
  const navigate = navigateSafe(useNavigate())

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [discardError, setDiscardError] = useState<string | null>(null)
  const [epicDiscarded, setEpicDiscarded] = useState(false)
  const [draftOwnerDisplayName, setDraftOwnerDisplayName] = useState<string | null>(null)

  useEffect(() => {
    if (!confirmOpen) {
      setDraftOwnerDisplayName(null)
      return
    }
    if (draftOwnerMetadataPending) {
      return
    }
    const ownerId = draftOwnerUserId?.trim()
    const selfId = currentUserId?.trim()
    if (!ownerId || !selfId || ownerId === selfId) {
      setDraftOwnerDisplayName(null)
      return
    }
    let cancelled = false
    void resolveDraftOwnerDisplayName(ownerId)
      .then((name) => {
        if (cancelled) return
        setDraftOwnerDisplayName(name)
      })
      .catch(() => {
        if (cancelled) return
        setDraftOwnerDisplayName(ownerId)
      })
    return () => {
      cancelled = true
    }
  }, [confirmOpen, draftOwnerMetadataPending, draftOwnerUserId, currentUserId])

  // Safety facts (owner id + updated_at) come from session metadata; display name is cosmetic only.
  const confirmReady = !draftOwnerMetadataPending

  const navigateAfterDiscard = useCallback(() => {
    if (authFlow === 'epic') {
      setEpicDiscarded(true)
      return
    }
    navigate('/orders', {
      replace: true,
      state: { discardConfirmed: true, discardMessage: 'Draft order discarded.' },
    })
  }, [authFlow, navigate])

  const navigateAfterSessionGone = useCallback(
    (message: string) => {
      if (authFlow === 'epic') {
        setEpicDiscarded(true)
        return
      }
      navigate('/orders', {
        replace: true,
        state: { sessionGoneMessage: message },
      })
    },
    [authFlow, navigate]
  )

  const handleSessionGone = useCallback(() => {
    // C1: provenance-aware passive 404 handling.
    // The marker records "we discarded this draft", so only write it where that is true.
    const markerAlreadyExists = isDiscardMarkerSet(patientLookupId)

    if (markerAlreadyExists) {
      // This tab or another already discarded — marker stays, show discard message
      dispatch(resetCanonicalOrder())
      onClearLocalOrderContext?.()
      navigateAfterDiscard()
    } else {
      // Passive 404 with no pre-existing marker — do NOT write marker, show neutral message
      dispatch(resetCanonicalOrder())
      onClearLocalOrderContext?.()
      navigateAfterSessionGone('This draft order is no longer available.')
    }
  }, [dispatch, navigateAfterDiscard, navigateAfterSessionGone, onClearLocalOrderContext, patientLookupId])

  const performDiscard = useCallback(async () => {
    const sid = sessionId?.trim()
    if (!sid) {
      setDiscardError('No active draft session to discard.')
      return
    }
    // Without a patient id the marker cannot be written, and /order/start would silently
    // recreate the draft the provider just discarded.
    if (!patientLookupId?.trim()) {
      setDiscardError(DRAFT_DETAILS_UNAVAILABLE_MESSAGE)
      return
    }

    setIsDiscarding(true)
    setDiscardError(null)

    try {
      // 1. Cancel pending autosave and abort in-flight PATCH before DELETE.
      cancelAutosave()

      const client = ProviderAPIClient.getInstance()
      await client.deleteOrderingSession(sid)

      // Marker only after DELETE succeeds (204 or 404 = draft is gone).
      applyDiscardMarker(patientLookupId)

      dispatch(resetCanonicalOrder())
      onClearLocalOrderContext?.()
      setConfirmOpen(false)
      navigateAfterDiscard()
    } catch (err) {
      if (err instanceof OrderingSessionDiscardConflictError) {
        setDiscardError(DISCARD_CONFLICT_MESSAGES[err.kind])
        return
      }
      if (err instanceof OrderingSessionGoneError) {
        applyDiscardMarker(patientLookupId)
        dispatch(resetCanonicalOrder())
        onClearLocalOrderContext?.()
        setConfirmOpen(false)
        navigateAfterDiscard()
        return
      }
      setDiscardError(
        err instanceof Error
          ? `${err.message} You can try again — your draft is still saved.`
          : 'Could not discard the order. You can try again — your draft is still saved.'
      )
    } finally {
      setIsDiscarding(false)
    }
  }, [cancelAutosave, dispatch, navigateAfterDiscard, onClearLocalOrderContext, patientLookupId, sessionId])

  const openDiscardConfirm = useCallback(() => {
    setDiscardError(null)
    setConfirmOpen(true)
  }, [])

  const closeDiscardConfirm = useCallback(() => {
    if (isDiscarding) return
    setConfirmOpen(false)
  }, [isDiscarding])

  const clearDiscardError = useCallback(() => setDiscardError(null), [])

  return {
    confirmOpen,
    isDiscarding,
    discardError,
    epicDiscarded,
    draftOwnerDisplayName,
    confirmReady,
    openDiscardConfirm,
    closeDiscardConfirm,
    performDiscard,
    handleSessionGone,
    clearDiscardError,
  }
}

/** Allows tests to inject navigate without router when needed. */
function navigateSafe(navigate: ReturnType<typeof useNavigate>) {
  return navigate
}

export { OrderingSessionGoneError }
